import React, { useState, useEffect } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import "./FinancialStatement.css";
import { styled } from "@mui/system";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const useStyles = makeStyles(() => ({
	financialStatement: {
		"& tbody tr:nth-of-type(even)": {
			backgroundColor: "#f9f9f9", // Sets background color for even rows
		},
		"& tbody tr:nth-of-type(odd)": {
			backgroundColor: "#fff",
		},
		"& tbody tr:hover": {
			backgroundColor: "#f1f1f1",
		},
		"& tbody td": {
			textAlign: "right",
			"&:first-child": {
				textAlign: "left",
			},
		},
	},
	headerRow: {
		"& .MuiTableCell-root": {
			textAlign: "right",
			fontWeight: "bold",
			color: "white",
			"&:first-child": {
				textAlign: "left",
			},
		},
	},
}));

const StickyTableCell = styled(TableCell)({
	position: "sticky",
	left: 0, // Fix position on the left
	background: "inherit",
	zIndex: 1, // Ensure it stays above other cells
	minWidth: 80,
});

export default function FinancialStatement({
	fetchDataFunction,
	statement,
	symbol,
}) {
	const [data, setData] = useState(null); // State to hold the fetched data
	const classes = useStyles(); // Call useStyles to get the classes object
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Fetch data when the component mounts
	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await fetchDataFunction(symbol); // Replace with your actual data-fetching function
				setData(response); // Update state with the fetched data
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};

		fetchData();
	}, [fetchDataFunction, symbol]); // Empty dependency array ensures this only runs on mount

	if (!data) {
		return (
			<Box
				sx={{
					display: "flex",
					marginTop: "7rem",
					justifyContent: "center",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	// Get all the years
	const years = Object.keys(data).sort((a, b) => b - a);

	// Get the first year's keys to display as "Cash Flow" categories
	const metrics = Object.keys(data[years[0]]);

	// Helper function to format numbers
	const formatNumber = (number) => {
		if (typeof number === "string") {
			return number;
		} else if (Math.abs(number) >= 1.0e9) {
			return `$${(number / 1.0e9).toFixed(2)}B`; // Billions
		} else if (Math.abs(number) >= 1.0e6) {
			return `$${(number / 1.0e6).toFixed(2)}M`; // Millions
		} else if (Math.abs(number) >= 1.0e3) {
			return `$${(number / 1.0e3).toFixed(2)}K`; // Thousands
		} else {
			return `$${number}`; // Less than thousands, just return the number
		}
	};

	return (
		<TableContainer
			component={Paper}
			elevation={2}
			sx={{
				maxWidth: "92vw",
				marginTop: isMobile ? "1.5rem" : "2.5rem",
				marginLeft: "auto",
				marginRight: "auto",
				marginBottom: "5rem",
			}}
		>
			<Table className={classes.financialStatement}>
				<TableHead>
					<TableRow
						className={classes.headerRow}
						sx={{
							// backgroundColor: "info.dark",
							bgcolor: "primary.main",
						}}
					>
						<StickyTableCell>{statement}</StickyTableCell>
						{years.map((year) => (
							<TableCell key={year}>{year}</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{metrics.map((metric) => (
						<TableRow
							key={metric}
							className={`${classes.rowHover} ${
								data[years[0]][metric][1]
							}`}
							sx={{
								"&:last-child td, &:last-child th": {
									border: 0,
								},
							}}
						>
							<StickyTableCell
								sx={{ paddingTop: "8px", paddingBottom: "8px" }}
							>
								{metric}
							</StickyTableCell>
							{years.map((year) => (
								<TableCell
									key={`${metric}-${year}`}
									sx={{
										paddingTop: "8px",
										paddingBottom: "8px",
									}}
								>
									{formatNumber(data[year][metric][0])}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
